import React from 'react';
import {useTranslation} from 'react-i18next';

import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import {alpha} from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import Notification from '../util/Notification';
import {mislecAPI} from "../../util/axios";

function Customisation({ open, onClose, user, updateUser }) {
    const [prompt, setPrompt] = React.useState(user.custom_prompt || '');
    const [similarity, setSimilarity] = React.useState(user.similarity || 5);
    const [notification, setNotification] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const { t } = useTranslation();

    const customise = () => {
        if (prompt.length > 1500) {
            setNotification(true);
            setMessage(t('header.customisation.error.prompt'));
            setSeverity('warning');
            return;
        }

        //  Data preparation
        const data = {
            ...user,
            'custom_prompt': prompt ? prompt : null,
            'similarity': similarity
        }

        //  Update custom prompt on the server
        mislecAPI.put(`/api/users/personalisation/${user._id}`, data)
            .then((res) => {
                mislecAPI.get(`/api/auth/refresh`)
                    .then((res) => {
                        //  Update access token and user data
                        sessionStorage.setItem('mISLec_access_token', res.data.access_token);
                        updateUser(JSON.parse(atob(res.data.access_token.split('.')[1])).user);
                    })
                    .catch((error) => {
                        setMessage(error.response.data.detail);
                        setSeverity('error');
                        setNotification(true);
                    });
                setMessage(res.data.detail);
                setSeverity('success');
                setNotification(true);
                setTimeout(() => {
                    onClose();
                }, 2500);
            })
            .catch((error) => {
                setMessage(error.response.data.detail);
                setSeverity('error');
                setNotification(true);
            });
    }

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: { xs: '100%', md: '50%' }
                }}
            >
                <Paper
                    sx={{
                        padding: '24px',
                        minWidth: { xs: '95%', md: '50%' },
                        maxWidth: { xs: '95%', md: '50%' }
                    }}
                >
                    <Typography variant='h6' sx={{ mb: 2 }}>
                        {t('header.customisation.title')}
                    </Typography>
                    <Divider />
                    <Typography variant='body2' sx={{ fontWeight: '600', mt: 2, mb: 1 }}>
                        {t('header.customisation.similarity.title')}
                    </Typography>
                    <Typography variant='body2' sx={{ fontWeight: '400', my: 1 }}>
                        {t('header.customisation.similarity.description')}
                    </Typography>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        value={similarity}
                        onChange={(e, value) => setSimilarity(value)}
                    >
                        <ToggleButton value={3}>
                            3
                        </ToggleButton>
                        <ToggleButton value={5}>
                            5
                        </ToggleButton>
                        <ToggleButton value={7}>
                            7
                        </ToggleButton>
                        <ToggleButton value={10}>
                            10
                        </ToggleButton>
                        <ToggleButton value={15}>
                            15
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant='body2' sx={{ fontWeight: '600', mt: 2, mb: 1 }}>
                        {t('header.customisation.prompt.title')}
                    </Typography>
                    <Typography variant='body2' sx={{ fontWeight: '400', my: 1 }}>
                        {t('header.customisation.prompt.description')}
                    </Typography>
                    <TextField
                        id='mISLec_custom_prompt'
                        name='mISLec_custom_prompt'
                        variant='outlined'
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        error={prompt.length > 1500}
                        fullWidth
                        multiline
                        rows={5}
                    />
                    <Stack
                        direction='row'
                        alignItems='center'
                        sx={{
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography
                            variant='caption'
                            sx={{
                                color: prompt.length <= 1500 ? 'text.secondary' : 'error.main',
                                ml: 0.5
                            }}
                        >
                            {prompt.length}/1500
                        </Typography>
                    </Stack>
                    <Stack
                        direction='row'
                        alignItems='center'
                        mt={2}
                        sx={{
                            justifyContent: {
                                xs: 'space-between',
                                md: 'flex-end'
                            }
                        }}
                    >
                        <Button
                            variant=''
                            onClick={onClose}
                            sx={{
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.error.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'error.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'error.main',
                                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08)
                                }
                            }
                            }
                        >
                            {t('header.customisation.buttons.cancel')}
                        </Button>
                        <Button
                            variant=''
                            onClick={() => customise()}
                            sx={{
                                ml: {
                                    xs: 0,
                                    md: 1
                                },
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => ({
                                    xs: alpha(theme.palette.primary.main, 0.08),
                                    md: 'rgba(33, 43, 54, 0.08)'
                                }),
                                color: {
                                    xs: 'primary.main',
                                    md: 'inherit'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                                }
                            }}
                        >
                            {t('header.customisation.buttons.save')}
                        </Button>
                    </Stack>
                    <Notification open={notification} message={message} severity={severity} onClose={() => setNotification(false)} duration={1500} />
                </Paper>
            </Modal>
        </>
    );
}

export default Customisation;